import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HighlightReel — top-reacted public events for a user (badge unlocks +
 * workout/recipe shares). Surfaces the "most-loved moments" as social proof.
 *
 * Props:
 *   handle?: string — looks up by public handle (anonymous viewers OK)
 *   self?: boolean — fetches the caller's own highlights via /highlights/me
 *   limit?: number (default 5)
 *   className?: string
 */
export default function HighlightReel({ handle, self, limit = 5, className = "" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = self
          ? `${API}/highlights/me?limit=${limit}`
          : `${API}/highlights/by-handle/${encodeURIComponent(handle)}?limit=${limit}`;
        const { data } = await axios.get(url);
        if (cancelled) return;
        setItems(data?.highlights || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handle, self, limit]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div data-testid="highlight-reel" className={`bg-[#0F0F0F] border border-zinc-900 p-5 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={14} className="text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
          Highlight Reel · Most Loved
        </span>
      </div>
      <ul className="space-y-2" data-testid="highlight-reel-list">
        {items.map((h, i) => (
          <li
            key={h.event_id}
            data-testid={`highlight-${i + 1}`}
            className={
              "flex items-center justify-between gap-3 px-3 py-2 transition-colors " +
              (i === 0 ? "bg-gradient-to-r from-[#D4AF37]/15 to-transparent border-l-2 border-[#D4AF37]" : "border-l-2 border-transparent")
            }
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className={"text-xs font-bold tabular-nums w-5 " + (i === 0 ? "text-[#D4AF37]" : "text-neutral-500")}>
                #{i + 1}
              </span>
              <div className="min-w-0">
                <div className="text-white font-bold truncate flex items-center gap-1.5">
                  {h.kind === "badge" ? (
                    <>
                      <span>{h.badge_icon || "🏆"}</span>
                      <span className="truncate">{h.badge_title || "Badge earned"}</span>
                    </>
                  ) : h.kind === "recipe_share" ? (
                    <>
                      <span>🍳</span>
                      <span className="truncate">{h.payload?.title || "Shared a recipe"}</span>
                    </>
                  ) : (
                    <>
                      <span>📋</span>
                      <span className="truncate">{h.payload?.title || "Shared a workout"}</span>
                    </>
                  )}
                  {i === 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest font-bold text-[#D4AF37] flex-shrink-0">
                      <Sparkles size={10} /> Top
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {Object.entries(h.reactions || {})
                    .filter(([, v]) => v.count > 0)
                    .map(([emoji, v]) => (
                      <span
                        key={emoji}
                        className="inline-flex items-center gap-0.5 text-xs bg-zinc-900 px-1.5 py-0.5"
                      >
                        <span>{emoji}</span>
                        <span className="tabular-nums text-[10px] text-neutral-400">{v.count}</span>
                      </span>
                    ))}
                </div>
              </div>
            </div>
            <div className="text-right whitespace-nowrap flex-shrink-0">
              <div className="text-[#D4AF37] font-bold tabular-nums">{h.total_reactions}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-600">love</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
